/**
 * Hue API v2 State Conversion Utilities
 * Converts internal light state format to v2 API payload structures
 */

import type { LightState } from '@/services/realtime/LightPresenceService'

/**
 * v2 API light state payload structure
 */
export interface HueV2LightState {
  on?: {
    on: boolean
  }
  dimming?: {
    brightness: number // 0-100 (percentage)
  }
  color?: {
    xy: {
      x: number // 0-1
      y: number // 0-1
    }
  }
  color_temperature?: {
    mirek: number // Mired color temperature
  }
  dynamics?: {
    duration: number // milliseconds
  }
}

/**
 * v2 API zone creation payload
 */
export interface HueV2ZoneCreate {
  type: 'zone'
  metadata: {
    name: string
    archetype?: string
  }
  children: Array<{
    rid: string
    rtype: 'light'
  }>
}

/**
 * v2 API grouped_light control payload
 */
export interface HueV2GroupedLightState {
  on?: {
    on: boolean
  }
  dimming?: {
    brightness: number
  }
  color?: {
    xy: {
      x: number
      y: number
    }
  }
  color_temperature?: {
    mirek: number
  }
  dynamics?: {
    duration: number
  }
}

/**
 * Convert internal LightState to v2 API payload
 * @param state Internal light state
 * @param transitionTime Optional transition time in deciseconds (v1 format)
 * @returns v2 API payload
 */
export function convertToV2LightState(
  state: Partial<LightState>,
  transitionTime: number = 4
): HueV2LightState {
  const payload: HueV2LightState = {}

  // On/Off state
  if (state.on !== undefined) {
    payload.on = { on: state.on }
  }

  // Brightness (convert from 0-254 to 0-100 percentage)
  if (state.brightness !== undefined) {
    payload.dimming = {
      brightness: Math.round((state.brightness / 254) * 100)
    }
  }

  // XY Color (already in 0-1 range)
  if (state.xy && state.xy.length === 2) {
    payload.color = {
      xy: {
        x: state.xy[0],
        y: state.xy[1]
      }
    }
  }

  // Color Temperature (mired)
  if (state.ct !== undefined) {
    payload.color_temperature = {
      mirek: state.ct
    }
  }

  // Transition time (convert from deciseconds to milliseconds)
  if (transitionTime > 0) {
    payload.dynamics = {
      duration: transitionTime * 100
    }
  }

  return payload
}

/**
 * Convert v2 API light response to internal LightState
 * @param v2Light v2 API light resource
 * @returns Internal light state
 */
export function convertFromV2LightState(v2Light: Record<string, unknown>): Partial<LightState> {
  const state: Partial<LightState> = {}

  // On/Off
  const onState = v2Light.on as { on?: boolean } | undefined
  if (onState?.on !== undefined) {
    state.on = onState.on
  }

  // Brightness (convert from percentage to 0-254)
  const dimming = v2Light.dimming as { brightness?: number } | undefined
  if (dimming?.brightness !== undefined) {
    state.brightness = Math.round((dimming.brightness / 100) * 254)
  }

  // XY Color
  const color = v2Light.color as { xy?: { x: number; y: number } } | undefined
  if (color?.xy) {
    state.xy = [color.xy.x, color.xy.y]
  }

  // Color Temperature
  const colorTemp = v2Light.color_temperature as { mirek?: number } | undefined
  if (colorTemp?.mirek !== undefined) {
    state.ct = colorTemp.mirek
  }

  // Hue/Saturation (v2 doesn't use these, but we can derive from XY if needed)
  // For now, leave them undefined and rely on XY color space

  return state
}

/**
 * Parse v1 light ID from v2 id_v1 field
 * @param id_v1 v2 id_v1 field (e.g., "/lights/21")
 * @returns v1 light ID (e.g., "21")
 */
export function parseV1LightId(id_v1: string): string | null {
  const match = id_v1.match(/^\/lights\/(\d+)$/)
  return match ? match[1] : null
}

/**
 * Create a temporary zone for group operations
 * @param name Zone name
 * @param lightRids Array of light resource IDs (v2 UUIDs)
 * @returns Zone creation payload
 */
export function createZonePayload(name: string, lightRids: string[]): HueV2ZoneCreate {
  return {
    type: 'zone',
    metadata: {
      name,
      archetype: 'computer' // Generic archetype for temporary zones
    },
    children: lightRids.map(rid => ({
      rid,
      rtype: 'light'
    }))
  }
}
